export const TIME_OPTION_FLOW = {
  JOB_START: 'jobStart',
  JOB_END: 'jobEnd',
  SIGN_IN: 'signIn',
};

// Placeholder rows for skeleton/failure states. Job-start's third slot is
// resolved per render via resolveJobStartThirdKey so the placeholder shows
// the real label the API will pick, not a generic one.
const FLOW_KEYS = {
  [TIME_OPTION_FLOW.JOB_START]: ['NOW', 'LAST_ACTIVE'],
  [TIME_OPTION_FLOW.JOB_END]: ['NOW', 'LAST_PART_PRODUCED', 'LAST_ACTIVE'],
  [TIME_OPTION_FLOW.SIGN_IN]: ['NOW', 'LAST_SIGN_OUT', 'START_OF_SHIFT'],
};

const LABELS = {
  NOW: 'Now',
  LAST_ACTIVE: 'Last active',
  LAST_PART_PRODUCED: 'Last part produced at',
  LAST_SIGN_OUT: 'Last Sign Out',
  START_OF_SHIFT: 'Start Of Shift',
  SINCE_LAST_JOB: 'Since last job',
  SINCE_SIGN_IN: 'Since sign in',
};

/**
 * Picks the job-start third option the same way the pre-time-options client
 * (and the API) does: whichever of last job end / sign-in is more recent,
 * falling back to SINCE_SIGN_IN when neither timestamp exists.
 */
export function resolveJobStartThirdKey({ lastJobEndAt, signInAt } = {}) {
  if (lastJobEndAt && signInAt) {
    return new Date(lastJobEndAt) >= new Date(signInAt)
      ? 'SINCE_LAST_JOB'
      : 'SINCE_SIGN_IN';
  }
  if (lastJobEndAt) return 'SINCE_LAST_JOB';
  return 'SINCE_SIGN_IN';
}

function keyToId(key) {
  return key === 'NOW' ? 'now' : key.toLowerCase().replaceAll('_', '-');
}

function makeRow({ key, value, disabled, loading, unavailable, t }) {
  let placeholder = null;
  if (loading) {
    placeholder = `${t('Loading')}…`;
  } else if (unavailable) {
    placeholder = t('Unavailable');
  }

  return {
    id: keyToId(key),
    key,
    label: LABELS[key] ? t(LABELS[key]) : key,
    value,
    disabled,
    loading: loading ?? false,
    unavailable: unavailable ?? false,
    placeholder,
  };
}

export function buildTimeOptionsPayload(body, flow, context = {}) {
  const flowPayload = body?.[flow] ?? {};

  if (flow === TIME_OPTION_FLOW.JOB_START) {
    return {
      ...body,
      [flow]: {
        ...flowPayload,
        lastActivityStartAt: context.latestActivity?.start,
        lastActivityEndAt: context.latestActivity?.end,
        lastNonIdleStartAt: context.latestNonIdleInterval?.start,
      },
    };
  }

  if (flow === TIME_OPTION_FLOW.JOB_END) {
    return {
      ...body,
      [flow]: {
        ...flowPayload,
        lastActivityStartAt: context.latestActivity?.start,
        lastActivityEndAt: context.latestActivity?.end,
        lastNonIdleEndAt: context.latestNonIdleInterval?.end,
      },
    };
  }

  return { ...body, [flow]: { ...flowPayload } };
}

function buildOptions(flow, t, nowISO, mode, { jobStartThirdKey } = {}) {
  let keys = FLOW_KEYS[flow] ?? [];
  if (flow === TIME_OPTION_FLOW.JOB_START) {
    keys = [...keys, jobStartThirdKey ?? 'SINCE_SIGN_IN'];
  }

  return keys.map((key) => {
    const isNow = key === 'NOW';
    return makeRow({
      key,
      value: isNow ? nowISO : null,
      disabled: !isNow,
      loading: !isNow && mode === 'skeleton',
      unavailable: !isNow && mode === 'failure',
      t,
    });
  });
}

export function buildSkeletonOptions(flow, t, nowISO, opts) {
  return buildOptions(flow, t, nowISO, 'skeleton', opts);
}

export function buildFailureOptions(flow, t, nowISO, opts) {
  return buildOptions(flow, t, nowISO, 'failure', opts);
}

export function mapApiOptionsToUiOptions(apiOptions, flow, t, nowISO) {
  // If the API disables every option (including NOW), the operator would be
  // stuck with nothing selectable — treat it as a failure state instead,
  // which keeps NOW usable and marks the rest unavailable.
  if (
    apiOptions.length > 0 &&
    apiOptions.every((option) => {
      return option.disabled;
    })
  ) {
    // Keep the third-row label the API actually returned (jobStart flow).
    const jobStartThirdKey = apiOptions.find((option) => {
      return option.key === 'SINCE_LAST_JOB' || option.key === 'SINCE_SIGN_IN';
    })?.key;

    return buildFailureOptions(flow, t, nowISO, { jobStartThirdKey });
  }

  return apiOptions.map((option) => {
    const isNow = option.key === 'NOW';
    const hasValue = isNow || !!option.value;
    return makeRow({
      key: option.key,
      value: isNow ? nowISO : option.value,
      disabled: option.disabled || !hasValue,
      loading: false,
      unavailable: !hasValue,
      t,
    });
  });
}

export function getFlowOptionsFromResponse(response, flow) {
  return response?.[flow]?.options ?? null;
}
